/** Prototype / fallback plant names — exclude from plant-code resolution. */
const DUMMY_PLANT_NAMES = new Set([
  "DEFAULT PLANT",
  "PLANT A",
  "PLANT B",
  "MANUFACTURING PLANT 1",
  "PRODUCTION FACILITY",
]);

export function normalizePlantLabel(v) {
  return String(v == null ? "" : v)
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();
}

/**
 * Resolve one SAP / invoice plant label to PlantMaster.PlantCode (exact or prefix match).
 */
export function resolvePlantCodeFromLabel(label, plants) {
  const pn = normalizePlantLabel(label);
  if (!pn || DUMMY_PLANT_NAMES.has(pn)) return null;

  const list = Array.isArray(plants) ? plants : [];
  let bestCode = null;
  let bestNameLen = 0;

  for (const p of list) {
    const code = String(p.plantCode ?? p.PlantCode ?? "").trim();
    const masterName = normalizePlantLabel(p.plantName ?? p.PlantName ?? "");
    if (!code || !masterName) continue;

    if (pn === masterName) return code;

    const matchesPrefix =
      pn.startsWith(`${masterName} `) ||
      pn.startsWith(masterName) ||
      pn.split(" ")[0] === masterName;
    if (matchesPrefix && masterName.length > bestNameLen) {
      bestCode = code;
      bestNameLen = masterName.length;
    }
  }

  return bestCode;
}

/** Map invoice / SR lines to distinct PlantMaster plant codes. */
export function getPlantCodesFromInvoiceLines(lines, plants) {
  const list = Array.isArray(plants) ? plants : [];
  const codes = new Set();
  const codeSet = new Set(
    list.map((p) => String(p.plantCode ?? p.PlantCode ?? "").trim()).filter(Boolean)
  );
  const byPlantId = new Map();
  for (const p of list) {
    const code = String(p.plantCode ?? p.PlantCode ?? "").trim();
    const pid = String(p.plantId ?? p.PlantId ?? p.PLANTID ?? "").trim();
    if (pid && code) byPlantId.set(pid, code);
  }

  for (const it of lines || []) {
    const direct = String(it.plantCode ?? it.PlantCode ?? "").trim();
    if (direct && codeSet.has(direct)) {
      codes.add(direct);
      continue;
    }
    const sapPlantId = String(
      it.productPlantId ?? it.ProductPlantId ?? it.plantId ?? it.PlantId ?? ""
    ).trim();
    if (sapPlantId && byPlantId.has(sapPlantId)) {
      codes.add(byPlantId.get(sapPlantId));
      continue;
    }
    const label =
      it.plantName ??
      it.PlantName ??
      it.productPlantDescription ??
      it.ProductPlantDescription ??
      "";
    const hit = resolvePlantCodeFromLabel(label, list);
    if (hit) codes.add(hit);
  }
  return [...codes];
}

export function buildPlantCodesCsvFromLines(lines, plants) {
  const codes = getPlantCodesFromInvoiceLines(lines, plants);
  return codes.length > 0 ? codes.join(",") : null;
}

/** Material Section rows chosen by the user (IsSelected !== 0). */
export function filterSelectedMaterialInvoiceLines(lines) {
  return (lines || []).filter(
    (d) => d && d.IsSelected !== 0 && d.isSelected !== 0
  );
}

/** Plant codes from selected material rows only (for @PlantCodesCsv on final submit). */
export function buildPlantCodesCsvFromSelectedLines(lines, plants) {
  const selected = filterSelectedMaterialInvoiceLines(lines);
  const source = selected.length > 0 ? selected : lines || [];
  return buildPlantCodesCsvFromLines(source, plants);
}
